// Course data for Digital Safety Workbook
export const courseModules = [
  {
    id: 1,
    slug: "introduction",
    title: "Introduction & How to Use",
    shortTitle: "Introduction",
    icon: "BookOpen",
    description: "Welcome to your digital safety journey",
    estimatedTime: "5 min",
    content: {
      intro: "Welcome to the Digital Safety Workbook! Online safety isn't about being perfect or knowing everything—it's about building good habits, staying aware, and knowing when to ask for help.",
      sections: [
        {
          title: "What This Course Helps You Do",
          content: [
            "Protect yourself and your family online",
            "Spot scams before they catch you",
            "Set up your devices safely",
            "Know what to do if something goes wrong",
            "Feel confident using technology"
          ]
        },
        {
          title: "How to Use This Course",
          content: [
            "Work at your own pace—there's no rush",
            "Complete the checklists and exercises",
            "Save your progress as you go",
            "Come back anytime to review",
            "Ask for help if you need it"
          ]
        }
      ],
      tip: {
        mascot: "koala",
        text: "You don't need to know everything. You just need to know what matters!"
      }
    },
    quiz: {
      question: "What is online safety really about?",
      options: [
        "Being a computer expert",
        "Building good habits and staying aware",
        "Never using the internet",
        "Buying expensive security software"
      ],
      correctIndex: 1,
      explanation: "Online safety is about building good habits, staying aware, and knowing when to ask for help. You don't need to be a tech expert!"
    }
  },
  {
    id: 2,
    slug: "strong-passwords",
    title: "Strong Passwords Made Simple",
    shortTitle: "Passwords",
    icon: "Lock",
    description: "Create passwords that protect you",
    estimatedTime: "10 min",
    content: {
      intro: "Your password is often the only thing standing between your information and someone who wants to steal it. A strong password can save you from a lot of trouble.",
      sections: [
        {
          title: "Why Passwords Really Matter",
          content: [
            "Your email password unlocks almost everything",
            "Banking passwords protect your money",
            "Weak passwords are the #1 way hackers get in",
            "One stolen password can lead to many problems"
          ]
        },
        {
          title: "What Makes a Strong Password",
          content: [
            "At least 12 characters long",
            "Unique for each important account",
            "Uses a passphrase (like 'BlueKoala!Summer$Tree')",
            "Not easy to guess (no birthdays, names, or 'password123')"
          ]
        }
      ],
      tip: {
        mascot: "koala",
        text: "Think of a passphrase like a short story: 'MyDog@Ate3Pizzas!' is much stronger than 'Spot2023'"
      },
      exercise: {
        type: "passphrase-builder",
        title: "Build Your Own Passphrase",
        instructions: "Try creating a 12+ character passphrase using random words, numbers, and symbols. Type it below to check its strength."
      }
    },
    checklist: [
      { id: "email-password", label: "Updated my email password" },
      { id: "banking-password", label: "Updated my banking password" },
      { id: "mygov-password", label: "Updated my MyGov password" },
      { id: "apple-google-password", label: "Updated my Apple ID or Google account password" }
    ],
    quiz: {
      question: "Which of these is the strongest password?",
      options: [
        "Password123",
        "john1965",
        "PurpleKangaroo!Jumps@Dawn",
        "qwerty"
      ],
      correctIndex: 2,
      explanation: "A passphrase like 'PurpleKangaroo!Jumps@Dawn' is strong because it's long, uses random words, includes symbols, and is hard to guess."
    }
  },
  {
    id: 3,
    slug: "device-passcodes",
    title: "Device Passcodes Made Simple",
    shortTitle: "Passcodes",
    icon: "Smartphone",
    description: "Lock your devices properly",
    estimatedTime: "8 min",
    content: {
      intro: "Your phone and tablet contain your entire life—photos, messages, banking apps, and personal information. A passcode is your first line of defence.",
      sections: [
        {
          title: "What a Passcode Is",
          content: [
            "The PIN or pattern you use to unlock your device",
            "Different from your account passwords",
            "Protects your device if it's lost or stolen"
          ]
        },
        {
          title: "Why This Matters",
          content: [
            "If your phone is stolen, a passcode protects your apps",
            "Banking apps are wide open without a passcode",
            "Your photos and messages stay private"
          ]
        },
        {
          title: "What Makes a Good Passcode",
          content: [
            "At least 6 digits (longer is better)",
            "Not easy to guess (avoid 123456, your birthday, or 000000)",
            "Not shared with anyone",
            "Combined with Face ID or fingerprint"
          ]
        }
      ],
      tip: {
        mascot: "kangaroo",
        text: "Enable auto-lock so your device locks itself after a minute of inactivity. That way you're protected even if you forget!"
      }
    },
    checklist: [
      { id: "passcode-on", label: "Passcode is turned on" },
      { id: "passcode-6-digits", label: "Passcode is at least 6 digits" },
      { id: "auto-lock", label: "Auto-lock is enabled" },
      { id: "face-id-fingerprint", label: "Face ID or fingerprint is enabled" }
    ],
    quiz: {
      question: "When should you change your device passcode?",
      options: [
        "Only when you get a new phone",
        "After your phone was lost, someone learned your code, or after a suspicious event",
        "Never—once set, it should stay the same",
        "Every day"
      ],
      correctIndex: 1,
      explanation: "Change your passcode if your phone was lost, if someone learned your code, or after a scam or suspicious event."
    }
  },
  {
    id: 4,
    slug: "two-step-verification",
    title: "Two-Step Verification (2FA)",
    shortTitle: "2FA",
    icon: "ShieldCheck",
    description: "Add an extra layer of protection",
    estimatedTime: "10 min",
    content: {
      intro: "Two-step verification (also called 2FA) adds a second check when you sign in. Even if someone steals your password, they can't get in without this second step.",
      sections: [
        {
          title: "What 2FA Is",
          content: [
            "A code sent to your phone when you log in",
            "Or a prompt in an app to approve the login",
            "Like having two locks on your door instead of one"
          ]
        },
        {
          title: "Why This Matters",
          content: [
            "Passwords alone aren't enough anymore",
            "Even if your password is stolen, 2FA keeps you safe",
            "Most major services now offer it"
          ]
        },
        {
          title: "Where to Turn It On First",
          content: [
            "Email (Gmail, Outlook, etc.)",
            "MyGov",
            "Your bank",
            "Apple ID or Google Account",
            "Social media (Facebook, Instagram)"
          ]
        }
      ],
      tip: {
        mascot: "koala",
        text: "App-based codes (like Google Authenticator or Microsoft Authenticator) are more secure than SMS codes. Hackers can sometimes intercept text messages."
      },
      warning: "Quick Tip: If a service offers authenticator app or SMS, choose the app option when possible—it's more secure."
    },
    checklist: [
      { id: "2fa-email", label: "2FA enabled on email" },
      { id: "2fa-mygov", label: "2FA enabled on MyGov" },
      { id: "2fa-bank", label: "2FA enabled on banking" },
      { id: "2fa-apple-google", label: "2FA enabled on Apple ID / Google Account" }
    ],
    quiz: {
      question: "Why is an authenticator app better than SMS for 2FA?",
      options: [
        "It's faster to type",
        "SMS codes can sometimes be intercepted by hackers",
        "Apps are free and SMS costs money",
        "There's no difference"
      ],
      correctIndex: 1,
      explanation: "SMS codes can sometimes be intercepted by hackers through SIM swapping or other techniques. Authenticator apps generate codes on your device, making them more secure."
    }
  },
  {
    id: 5,
    slug: "backups-recovery",
    title: "Backups & Recovery Basics",
    shortTitle: "Backups",
    icon: "CloudUpload",
    description: "Protect your precious memories",
    estimatedTime: "8 min",
    content: {
      intro: "Phones break. Computers crash. Things get lost. Regular backups mean you never lose your precious photos, contacts, or important information.",
      sections: [
        {
          title: "Why Backups Matter",
          content: [
            "Devices can break, get lost, or be stolen",
            "Ransomware can lock your files",
            "Backups let you recover everything quickly"
          ]
        },
        {
          title: "What Backups Protect",
          content: [
            "Your photos and videos",
            "Contact information",
            "Messages",
            "App data and settings",
            "Important documents"
          ]
        },
        {
          title: "How Backups Usually Work",
          content: [
            "iPhone: iCloud Backup (Settings > Your Name > iCloud > iCloud Backup)",
            "Android: Google Backup (Settings > System > Backup)",
            "Computer: External hard drive or cloud services like Google Drive or OneDrive"
          ]
        }
      ],
      tip: {
        mascot: "kangaroo",
        text: "Set your backups to run automatically—usually overnight while your device is charging. You'll never have to remember to do it!"
      }
    },
    checklist: [
      { id: "backups-on", label: "Backups are turned on" },
      { id: "photos-included", label: "Photos are included in backup" },
      { id: "contacts-included", label: "Contacts are included in backup" },
      { id: "backup-date-checked", label: "Checked last backup date" }
    ],
    fillInFields: [
      { id: "last-backup-date", label: "Last backup date", placeholder: "e.g., 15 January 2025" }
    ],
    quiz: {
      question: "What's the best way to make sure your backups happen?",
      options: [
        "Remember to do it manually every month",
        "Set them to run automatically",
        "Only backup when you get a new phone",
        "Ask a friend to remind you"
      ],
      correctIndex: 1,
      explanation: "Automatic backups (usually overnight while charging) ensure you're always protected without having to remember."
    }
  },
  {
    id: 6,
    slug: "something-wrong",
    title: "What to Do If Something Goes Wrong",
    shortTitle: "Emergency Steps",
    icon: "AlertTriangle",
    description: "Stay calm and take action",
    estimatedTime: "10 min",
    content: {
      intro: "If you think you've been scammed or something suspicious has happened, don't panic. Quick action can limit the damage. Here's what to do.",
      sections: [
        {
          title: "First Things First",
          content: [
            "Stop clicking—don't click any more links",
            "Disconnect from the internet if needed",
            "Don't delete anything yet (you may need it as evidence)"
          ]
        },
        {
          title: "If You Clicked a Link or Shared Details",
          content: [
            "Change your passwords immediately (start with email)",
            "Contact your bank if you shared financial details",
            "Turn on 2FA if you haven't already",
            "Check your accounts for unusual activity"
          ]
        },
        {
          title: "Report It",
          content: [
            "Scamwatch: www.scamwatch.gov.au",
            "ReportCyber: www.cyber.gov.au/report",
            "If money was lost, also report to police"
          ]
        }
      ],
      tip: {
        mascot: "koala",
        text: "Write down your bank's contact number NOW, before you need it. In an emergency, you don't want to be searching!"
      },
      warning: "Remember: Your bank will NEVER ask for your password, PIN, or one-time codes. If someone asks, it's a scam."
    },
    fillInFields: [
      { id: "bank-contact", label: "My bank's contact number", placeholder: "e.g., 13 22 21" },
      { id: "email-provider-help", label: "Email provider help", placeholder: "e.g., Gmail support" },
      { id: "trusted-helper", label: "My trusted helper (friend/family)", placeholder: "Name and phone number" }
    ],
    quiz: {
      question: "What's the FIRST thing you should do if you think you've been scammed?",
      options: [
        "Delete all the emails",
        "Stop clicking and don't interact further",
        "Wait a few days to see what happens",
        "Reply to ask if it's real"
      ],
      correctIndex: 1,
      explanation: "Stop immediately—don't click any more links or interact further. Then disconnect and start changing passwords."
    }
  },
  {
    id: 7,
    slug: "scams-phishing",
    title: "Spotting Scams & Phishing",
    shortTitle: "Scams",
    icon: "Eye",
    description: "Recognise tricks before they catch you",
    estimatedTime: "12 min",
    content: {
      intro: "Scammers are clever—they pretend to be trusted organisations and create urgency to stop you from thinking clearly. Learning to spot their tricks is your best defence.",
      sections: [
        {
          title: "Scams Often Pretend to Be",
          content: [
            "Your bank or financial institution",
            "Australia Post or couriers",
            "The ATO (tax office)",
            "Telstra, Optus, or other providers",
            "MyGov or Centrelink",
            "Tech support (Microsoft, Apple)"
          ]
        },
        {
          title: "Common Warning Signs",
          content: [
            "Unexpected messages you weren't expecting",
            "Urgency: 'Act now!' or 'Your account will be suspended!'",
            "Requests for passwords, PINs, or personal info",
            "Links that look slightly wrong",
            "Poor spelling or grammar",
            "Threats or scare tactics"
          ]
        },
        {
          title: "Stop, Think, Check",
          content: [
            "STOP: Don't rush. Scammers want you to act fast.",
            "THINK: Does this make sense? Was I expecting this?",
            "CHECK: Contact the organisation directly using their official website or phone number (not the one in the message)"
          ]
        }
      ],
      tip: {
        mascot: "kangaroo",
        text: "If unsure: Don't click. Don't reply. Ask someone you trust. You can always report suspicious messages to Scamwatch."
      }
    },
    checklist: [
      { id: "know-warning-signs", label: "I know the warning signs of scams" },
      { id: "stop-think-check", label: "I will Stop, Think, Check before clicking" },
      { id: "know-scamwatch", label: "I know how to report to Scamwatch" }
    ],
    quiz: {
      question: "You receive an urgent text saying 'Your bank account has been locked. Click here immediately to restore access.' What should you do?",
      options: [
        "Click the link quickly to fix it",
        "Don't click—call your bank directly using their official number",
        "Reply to ask for more information",
        "Forward it to friends to warn them"
      ],
      correctIndex: 1,
      explanation: "Never click links in unexpected messages. Contact your bank directly using the number on their official website or your bank card."
    }
  },
  {
    id: 8,
    slug: "safe-wifi",
    title: "Safe Wi-Fi Basics",
    shortTitle: "Wi-Fi Safety",
    icon: "Wifi",
    description: "Stay safe on networks",
    estimatedTime: "8 min",
    content: {
      intro: "Not all Wi-Fi is created equal. Public Wi-Fi at cafes, airports, and shopping centres can be risky because you don't know who else is on the network.",
      sections: [
        {
          title: "Why Wi-Fi Matters",
          content: [
            "On public Wi-Fi, others might be able to see what you're doing",
            "Hackers can set up fake Wi-Fi networks with familiar names",
            "Your home Wi-Fi is usually safe if properly secured"
          ]
        },
        {
          title: "Public Wi-Fi is Risky",
          content: [
            "Avoid banking or shopping on public Wi-Fi",
            "Don't enter passwords on public networks if you can help it",
            "If you must use public Wi-Fi, stick to browsing only"
          ]
        },
        {
          title: "Simple Rules",
          content: [
            "Use your mobile data for banking and sensitive tasks",
            "Never connect to unknown networks",
            "If a public Wi-Fi asks for personal details, be suspicious"
          ]
        }
      ],
      tip: {
        mascot: "koala",
        text: "When in doubt, use your phone's mobile data instead of public Wi-Fi. It's much safer for anything important."
      }
    },
    checklist: [
      { id: "home-wifi-password", label: "My home Wi-Fi has a password" },
      { id: "router-password-changed", label: "Router password is not the default" },
      { id: "wifi-not-shared-freely", label: "Wi-Fi password isn't shared with everyone" },
      { id: "avoid-public-wifi-banking", label: "I avoid banking on public Wi-Fi" }
    ],
    quiz: {
      question: "Which is the safest network to use for online banking?",
      options: [
        "Free Wi-Fi at a coffee shop",
        "'FREE AIRPORT WIFI'",
        "Your home Wi-Fi with a password",
        "An open network called 'TELSTRA_FREE'"
      ],
      correctIndex: 2,
      explanation: "Your password-protected home Wi-Fi is the safest option. Public and open networks can be monitored or faked by hackers."
    }
  },
  {
    id: 9,
    slug: "device-security-updates",
    title: "Device Security & Updates",
    shortTitle: "Updates",
    icon: "RefreshCw",
    description: "Keep your devices protected",
    estimatedTime: "8 min",
    content: {
      intro: "Software updates might seem annoying, but they're one of the most important things you can do to stay safe. Updates fix security holes that hackers know about.",
      sections: [
        {
          title: "Why Updates Matter",
          content: [
            "Updates fix security vulnerabilities",
            "Hackers actively target outdated software",
            "Updates also bring new features and fixes"
          ]
        },
        {
          title: "Healthy Device Habits",
          content: [
            "Install updates when prompted—don't delay",
            "Turn on automatic updates when possible",
            "Use trusted antivirus software (Windows has built-in protection)",
            "Don't ignore update notifications"
          ]
        }
      ],
      tip: {
        mascot: "kangaroo",
        text: "Set your devices to update automatically overnight. You'll wake up protected without any effort!"
      }
    },
    checklist: [
      { id: "phone-updated", label: "Phone software is up to date" },
      { id: "computer-updated", label: "Computer software is up to date" },
      { id: "tablet-updated", label: "Tablet software is up to date" },
      { id: "auto-updates-on", label: "Automatic updates are enabled" }
    ],
    fillInFields: [
      { id: "next-check-date", label: "Monthly reminder: next check date", placeholder: "e.g., 1st of each month" }
    ],
    quiz: {
      question: "Why should you install software updates promptly?",
      options: [
        "They make your device run slower",
        "They're optional and don't matter much",
        "They fix security holes that hackers can exploit",
        "The company just wants to annoy you"
      ],
      correctIndex: 2,
      explanation: "Updates fix known security vulnerabilities. Delaying updates leaves your device exposed to threats that hackers actively target."
    }
  },
  {
    id: 10,
    slug: "protecting-personal-info",
    title: "Protecting Your Personal Information",
    shortTitle: "Privacy",
    icon: "User",
    description: "Share smart, stay private",
    estimatedTime: "10 min",
    content: {
      intro: "Privacy is about control—deciding who sees your information and what you share. The less information available about you, the harder it is to misuse.",
      sections: [
        {
          title: "Smart Sharing Habits",
          content: [
            "Only share what's necessary",
            "Think before posting: could this be used against me?",
            "Be careful sharing location, birthday, or family details",
            "Adjust 'tag' settings so you approve posts before they appear"
          ]
        },
        {
          title: "Review Your Settings",
          content: [
            "Check who can see your posts (friends only vs. public)",
            "Review who can contact you or send requests",
            "See what information is visible on your profile"
          ]
        },
        {
          title: "Things to Check",
          content: [
            "Who can see my posts?",
            "Who can contact me?",
            "What information is public?",
            "Can people find me by searching my email or phone?"
          ]
        }
      ],
      tip: {
        mascot: "koala",
        text: "Pick one social media platform today and spend 10 minutes reviewing your privacy settings. You might be surprised what's public!"
      }
    },
    checklist: [
      { id: "reviewed-settings", label: "Reviewed privacy settings on one platform" },
      { id: "posts-friends-only", label: "Posts are visible to friends only (not public)" },
      { id: "limited-public-info", label: "Limited public information on profile" }
    ],
    fillInFields: [
      { id: "platform-reviewed", label: "Platform I reviewed today", placeholder: "e.g., Facebook, Instagram" }
    ],
    quiz: {
      question: "What's the safest privacy setting for your social media posts?",
      options: [
        "Public (everyone can see)",
        "Friends of friends",
        "Friends only",
        "It doesn't matter"
      ],
      correctIndex: 2,
      explanation: "Friends only is generally the safest default. Public posts can be seen by anyone, including scammers looking for personal details."
    }
  },
  {
    id: 11,
    slug: "safe-browsing",
    title: "Safe Browsing & Downloads",
    shortTitle: "Browsing",
    icon: "Globe",
    description: "Navigate the web safely",
    estimatedTime: "8 min",
    content: {
      intro: "Not all websites are safe. Learning to tell the difference—and avoiding risky downloads—is an important skill for staying safe online.",
      sections: [
        {
          title: "Safe Websites Have",
          content: [
            "'https://' in the address (the 's' means secure)",
            "A padlock icon in the browser bar",
            "A legitimate, recognisable web address"
          ]
        },
        {
          title: "Risky Habits to Avoid",
          content: [
            "Clicking pop-ups that say 'You've won!' or 'Your computer is infected!'",
            "Downloading software from unknown websites",
            "Clicking 'Allow' on random notification requests",
            "Ignoring browser warnings about unsafe sites"
          ]
        },
        {
          title: "Practice Safe Browsing",
          content: [
            "Check for 'https://' before entering any information",
            "Close pop-ups using the X, not by clicking them",
            "Only download from official sources (App Store, Google Play, official websites)"
          ]
        }
      ],
      tip: {
        mascot: "kangaroo",
        text: "Try it now: Go to a website you use often and look for the padlock and 'https://' in the address bar. That's how you know it's secure!"
      },
      exercise: {
        type: "website-checker",
        title: "Check a Website You Use",
        instructions: "Think of a website you visit regularly. Check if it has https:// and a padlock. Enter the website name below."
      }
    },
    checklist: [
      { id: "check-https", label: "I check for https:// before entering information" },
      { id: "close-popups-properly", label: "I close pop-ups using the X button" },
      { id: "official-downloads-only", label: "I only download from official sources" }
    ],
    quiz: {
      question: "What does 'https://' at the start of a website address mean?",
      options: [
        "The website loads faster",
        "The connection is secure and encrypted",
        "The website is Australian",
        "Nothing important"
      ],
      correctIndex: 1,
      explanation: "The 's' in 'https' stands for 'secure'. It means the connection between you and the website is encrypted, so others can't see what you're doing."
    }
  },
  {
    id: 12,
    slug: "family-community",
    title: "Family & Community Safety",
    shortTitle: "Family Safety",
    icon: "Users",
    description: "Protect your loved ones together",
    estimatedTime: "10 min",
    content: {
      intro: "Online safety isn't just personal—it's something we share with our families and communities. Talking openly about safety helps everyone stay protected.",
      sections: [
        {
          title: "Online Safety is Shared",
          content: [
            "Family members may have different skill levels",
            "Scammers often target people who seem vulnerable",
            "Talking about scams removes the shame and stigma",
            "We can all learn from each other's experiences"
          ]
        },
        {
          title: "Helpful Conversations to Have",
          content: [
            "Have you seen any suspicious messages lately?",
            "Do you know what to do if you get a scam call?",
            "Can I help you check your privacy settings?",
            "Let's set up 2FA together"
          ]
        },
        {
          title: "Create a Family Agreement",
          content: [
            "We'll tell each other about suspicious messages",
            "We won't share passwords (except with parents for kids)",
            "We'll ask before clicking links we're unsure about",
            "We won't feel embarrassed to ask for help"
          ]
        }
      ],
      tip: {
        mascot: "koala",
        text: "Today, share one thing you've learned from this course with a family member. It could protect them from a scam!"
      }
    },
    checklist: [
      { id: "talked-family-member", label: "Talked to a family member about online safety" },
      { id: "shared-safety-tip", label: "Shared one safety tip with someone" },
      { id: "offered-help", label: "Offered to help someone with their security settings" }
    ],
    fillInFields: [
      { id: "who-talked-to", label: "Who did you talk to?", placeholder: "e.g., Mum, partner, neighbour" }
    ],
    quiz: {
      question: "Why is it important to talk about online safety with family?",
      options: [
        "To show off how much you know",
        "To scare them about the internet",
        "Because we can all learn from each other and stay protected together",
        "It's not important—everyone should figure it out alone"
      ],
      correctIndex: 2,
      explanation: "Talking openly about online safety helps everyone stay protected. We all have different experiences and can learn from each other."
    }
  }
];

export const australianResources = [
  {
    name: "Scamwatch",
    url: "https://www.scamwatch.gov.au",
    description: "Report scams and learn about the latest scam tactics"
  },
  {
    name: "eSafety Commissioner",
    url: "https://www.esafety.gov.au",
    description: "Australia's online safety regulator - help with cyberbullying, image abuse, and more"
  },
  {
    name: "IDCARE",
    url: "https://www.idcare.org",
    description: "Free help if your identity has been compromised"
  },
  {
    name: "Australian Cyber Security Centre",
    url: "https://www.cyber.gov.au",
    description: "Report cyber incidents and get security advice"
  },
  {
    name: "ReportCyber",
    url: "https://www.cyber.gov.au/report",
    description: "Report cybercrime to the Australian Government"
  }
];

export const glossary = [
  {
    term: "Phishing",
    definition: "A scam where criminals pretend to be trusted organisations to trick you into revealing personal information, passwords, or financial details."
  },
  {
    term: "2FA (Two-Factor Authentication)",
    definition: "An extra security step where you need both your password AND another form of verification (like a code sent to your phone) to log in."
  },
  {
    term: "Passphrase",
    definition: "A password made up of multiple words strung together, making it longer and more secure than a traditional password."
  },
  {
    term: "HTTPS",
    definition: "A secure version of HTTP (how websites communicate). The 'S' means the connection is encrypted and safer for entering personal information."
  },
  {
    term: "Malware",
    definition: "Malicious software designed to damage, disrupt, or gain unauthorised access to your device or information."
  },
  {
    term: "Ransomware",
    definition: "A type of malware that locks your files or device and demands payment to unlock them."
  }
];
